import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { unitId, actionId } from '@/annotations'
import { Button } from '@/components/Button'
import { Note } from '@/components/Note'
import { Chip } from '@/components/Chip'
import { Icon } from '@/components/Icon'
import { Alert } from '@/components/Alert'
import { GatedControl } from '@/components/GatedControl'
import { TEXT_INPUT_CLASSNAME, TEXT_INPUT_STYLE } from '@/styles/formControls'
import { ApiProblemError } from '@/data/apiClient'
import { triggerBothBackups } from '@/data/triggerBackups'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { useActiveOrgId, useActiveWorkspaceId } from '@/data/store'
import { useCapabilitySet, hasWorkspaceCapability } from '@/data/capabilities'
import type { components } from '@/api/generated/schema'

type BackupKind = components['schemas']['BackupKind']

interface Category {
  readonly key: string
  readonly group: BackupKind
  readonly title: string
  readonly lead: string
  readonly captured: readonly string[]
  readonly limit: string
}

const GROUPS: readonly { readonly key: BackupKind; readonly title: string; readonly blurb: string }[] = [
  { key: 'DATA_BACKUP', title: 'Data backup', blurb: 'Repository content — forever incremental, on its own independent schedule.' },
  { key: 'CONFIG_BACKUP', title: 'Configuration backup', blurb: 'Workspace and repository settings — captured in full, on its own independent schedule.' },
]

// Prototype_v2.html `#v-backup`'s own `.pick` rows, regrouped under the two real jobs that
// actually run them (`design/05-backup-engine.md` §5.0/§5.1: "the data backup job captures git
// and PR bodies; the configuration backup job captures everything else"). The old flat 6-row list
// never distinguished which job produced which row, which is exactly what let the on-demand
// trigger below only ever fire `DATA_BACKUP` while four of these six categories are actually
// produced by `CONFIG_BACKUP` — pressing "Back up now" looked like it covered everything shown on
// the page but silently only ever backed up two of the six. Each category's old `notCaptured` list
// (pull request participants/approvals/comments) has been dropped entirely rather than shown as a
// disabled chip: this page's job is to say what is backed up today, not to catalogue gaps.
const CATEGORIES: readonly Category[] = [
  {
    key: 'git',
    group: 'DATA_BACKUP',
    title: 'Repositories and git history',
    lead: 'Treated as data: forever incremental, always encrypted, and addressed through the snapshot catalogue rather than a table',
    captured: ['Complete object set', 'Branches', 'Tags', 'Commit history', 'File tree with sizes', 'Git LFS objects'],
    limit:
      "This is the only class that is incremental. It is fetched with git's own haves/wants negotiation against the content-addressed store, on the 60,000/hour git budget rather than the 1,000/hour REST budget, so branches, tags and commit history cost no REST calls at all.",
  },
  {
    key: 'pr',
    group: 'DATA_BACKUP',
    title: 'Pull requests',
    lead: 'Captured in full in every snapshot, across all four states, for reading and for restore where the platform permits',
    captured: ['Open', 'Merged', 'Declined', 'Superseded'],
    limit: 'Merged, declined and superseded pull requests are captured and readable, and cannot be recreated: Bitbucket permits mutation of open pull requests only.',
  },
  {
    key: 'repo-config',
    group: 'CONFIG_BACKUP',
    title: 'Repository configuration',
    lead: "Captured in full for every repository in every configuration snapshot — nothing here is gated on whether the repository's code changed",
    captured: [
      'Repository settings and visibility',
      'Branching model',
      'Branch restrictions',
      'Restriction exemptions',
      'Repository permissions',
      'Default reviewers',
      'Deploy keys',
      'Repository webhooks',
      'Pipelines enabled',
      'Pipeline variables',
      'Pipeline schedules',
      'Deployment environments',
      'Environment variables',
      'SSH known hosts',
    ],
    limit:
      'Secured pipeline and deployment variable values are never returned by the API, so they are captured by key and scope only. Webhook secrets are not captured at all. Project-level branch restrictions have no Bitbucket Cloud API, so repository-level rules are the whole of it.',
  },
  {
    key: 'workspace-objects',
    group: 'CONFIG_BACKUP',
    title: 'Workspace objects',
    lead: 'Captured in full once per workspace in every configuration snapshot',
    captured: [
      'Workspace record',
      'Members',
      'Workspace permissions',
      'Workspace webhooks',
      'Groups and group membership',
      'Projects',
      'Project policy',
      'Repository membership of projects',
      'Workspace pipeline variables',
      'Self-hosted runners',
    ],
    limit:
      'Workspace membership and permissions are read-only in the API: captured and reported on, restorable by no product. Groups have no v2 API and are keyed on their slug, so a renamed group reads as a removal and an addition rather than as one change.',
  },
  {
    key: 'pipeline-history',
    group: 'CONFIG_BACKUP',
    title: 'Pipeline runs and deployment history',
    lead: 'Captured for forensics and reporting, and fenced last so it can never cost you your git history',
    captured: ['Pipeline run records', 'Deployment history', 'Environment', 'Deployed commit', 'State', 'Timestamp', 'Commit build statuses'],
    limit: 'Both are read-only in the Bitbucket API: captured for reading, and not restorable.',
  },
  {
    key: 'downloads',
    group: 'CONFIG_BACKUP',
    title: 'Downloads and snippets',
    lead: 'Binary side-cars, fetched, checksummed and stored with sanitized filenames',
    captured: ['Download artifacts', 'Snippet files', 'SHA-256 per file'],
    limit: 'Bitbucket refuses download upload and download on Free-plan workspaces, so this class is empty on a Free plan rather than failing.',
  },
]

const LIMITATIONS: readonly { readonly title: string; readonly note: string }[] = [
  { title: 'Secured pipeline variable values', note: 'Captured by key and secured flag only. Value never returned by the API.' },
  { title: 'Secured deployment variable values', note: 'Captured by key and secured flag only. Value never returned by the API.' },
  { title: 'Webhook secrets', note: 'Not captured. The secret is never returned by the API.' },
  { title: 'Workspace membership and permissions', note: 'Captured for reporting. Not writable by any product.' },
  { title: 'Merged, declined and superseded pull requests', note: 'Captured for reading. Only open pull requests can be recreated.' },
]

/**
 * `v-backup` — process/09-SCREEN-SPECS/v-backup.md, on-demand backup.
 *
 * **Rebuilt on direct product instruction (2026-09-25) — no longer matches that spec's original
 * "exactly one control, `DATA_BACKUP` only" reading.** Three things were wrong with the previous
 * build: (1) the six categories were a flat list with no indication of which of the two real jobs
 * produces each one, (2) every row carried a checkbox that was permanently `checked`/`readOnly` —
 * decorative, and actively misleading since it implied a per-category on/off the trigger below
 * never honored, (3) the trigger fired `DATA_BACKUP` alone, so pressing "Back up now" silently
 * skipped `repo-config`/`workspace-objects`/`pipeline-history`/`downloads` even though the page
 * displayed them right there as things this button was about to capture. Fixed by: grouping the
 * six categories under the two jobs that actually produce them (per `design/05-backup-engine.md`
 * §5.0/§5.1 — data backup: git + PR bodies; configuration backup: everything else), dropping the
 * checkbox entirely, and firing both `jobTriggerBackup` calls — one `DATA_BACKUP`, one
 * `CONFIG_BACKUP`, same tag, independent `Idempotency-Key`s each (`BackupTrigger.backup_kind` is
 * required per call; there is no combined endpoint, shared with `ObWz6.tsx` via
 * `data/triggerBackups.ts`) — from the one button. On success, routes to `/jobs/backup`
 * (`v-jobsbackup`) rather than showing an inline confirmation, so the admin lands directly on the
 * table where both freshly `PENDING` jobs appear — **and the jobs-list query is invalidated first**
 * (found live, 2026-09-25): that query defaults to a 10-minute `staleTime` (`data/query.ts`'s own
 * `POLICY.reference`), so without this, landing there right after triggering showed whatever the
 * list looked like on the LAST visit, not these new jobs, until a hard reload forced a real refetch.
 */
export function VBackup() {
  const orgId = useActiveOrgId()!
  const workspaceId = useActiveWorkspaceId()!
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const caps = useCapabilitySet()
  const [tag, setTag] = useState('')
  const [openCategory, setOpenCategory] = useState<string | undefined>(CATEGORIES[0]?.key)

  const trigger = useMutation({
    mutationFn: () => triggerBothBackups(orgId, workspaceId, tag),
    // The jobs list (`v-jobsbackup`) is `POLICY.reference`-staled (10 minutes, `data/query.ts`'s
    // own default) — without this, landing there right after triggering shows whatever was cached
    // from the LAST visit, not these new jobs, until that window lapses or a hard reload wipes the
    // cache (found live: looked exactly like the trigger silently did nothing). The same
    // `['backup-jobs', orgId, workspaceId]` prefix `VJobsbackup`'s own "Refresh" icon-button already
    // invalidates — this makes that refresh automatic instead of a manual extra click.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backup-jobs', orgId, workspaceId] })
      navigate('/jobs/backup')
    },
  })

  const problem = trigger.error instanceof ApiProblemError ? trigger.error.problem : undefined
  const allowed = hasWorkspaceCapability(caps, workspaceId, 'BACKUP_RUN')

  return (
    <div data-scr={unitId('v-backup')}>
      <h1 className="m-0 mb-8 text-section font-semibold">On-demand backup</h1>

      <div data-cmp="center-card" className="border border-bd rounded-2 py-7 px-8 mx-auto" style={{ maxWidth: 640 }}>
        <h3 className="m-0 mb-3 text-center text-body-lg font-semibold">Trigger a backup</h3>
        <Note>
          Start a backup now, outside the daily schedule. This runs both jobs below — the data backup and the configuration
          backup — decoupled and independently scheduled, so neither one waits on the other.
        </Note>

        <div data-cmp="picker" className="border border-bd rounded-2 overflow-hidden mt-6">
          {GROUPS.map((group) => (
            <div key={group.key}>
              <div data-cmp="picker-group-header" className="py-2 px-4 bg-panel border-b border-bd">
                <div className="text-small font-semibold">{group.title}</div>
                <div className="text-small text-tx2">{group.blurb}</div>
              </div>
              {CATEGORIES.filter((c) => c.group === group.key).map((c) => {
                const expanded = openCategory === c.key
                return (
                  <div key={c.key}>
                    <div
                      data-cmp="picker-row"
                      onClick={() => setOpenCategory(expanded ? undefined : c.key)}
                      className={`flex items-center gap-3 py-3 px-4 text-body cursor-pointer border-b border-bd last:border-b-0 ${expanded ? 'bg-row' : ''}`}
                    >
                      <span className="flex-1">{c.title}</span>
                      <Icon name={expanded ? 'i-chevU' : 'i-chevD'} size="s" color="muted" />
                    </div>
                    {expanded && (
                      <div data-cmp="picker-body" className="py-4 px-8 border-b border-bd last:border-b-0">
                        <p className="m-0 mb-3 text-small text-tx2">{c.lead}</p>
                        <div data-cmp="chip-row" className="flex flex-wrap gap-2">
                          {c.captured.map((label) => (
                            <Chip key={label} variant="plain">
                              {label}
                            </Chip>
                          ))}
                        </div>
                        <div data-cmp="limit-note" className="flex gap-3 items-start mt-4 text-small text-tx2">
                          <Icon name="i-alert" size="s" color="warn" />
                          <div>{c.limit}</div>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          ))}
        </div>

        {problem && (isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <Alert variant="danger">{problem.title}</Alert>)}

        <div className="flex items-center justify-center gap-4 mt-7">
          <input
            type="text"
            placeholder="Enter tag for this job"
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            className={TEXT_INPUT_CLASSNAME}
            style={{ ...TEXT_INPUT_STYLE, width: 230, textAlign: 'center' }}
          />
          <GatedControl allowed={allowed} reason="Available on the Operator role and above">
            <Button data-act={actionId('job.trigger')} disabled={trigger.isPending} onClick={() => trigger.mutate()}>
              Back up now
            </Button>
          </GatedControl>
        </div>
      </div>

      <div data-cmp="panel" className="bg-panel border border-bd rounded-2 p-6 mx-auto mt-7" style={{ maxWidth: 640 }}>
        <div data-cmp="panel-header" className="mb-4">
          <h3 className="m-0 text-body-lg font-semibold">Platform limitations that apply to this capture</h3>
        </div>
        <table className="w-full text-small border-collapse">
          <tbody>
            {LIMITATIONS.map((l) => (
              <tr key={l.title} className="border-b border-bd last:border-b-0">
                <td className="py-3 pr-5 pl-0 font-semibold align-top" style={{ width: '46%' }}>
                  {l.title}
                </td>
                <td className="py-3 px-0 text-tx2 align-top">{l.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-4">
          <Note>Each limitation is stated again on the restore confirmation step for any selection that includes it.</Note>
        </div>
      </div>
    </div>
  )
}
